import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalContent } from 'src/app/core/interface';
import { Contract, Reason, Regulation, ReasonTypes, User, AlertItem, AlertType } from 'src/app/core/models';
import {
  AlertService,
  ContractGlobalService, ContractService, ModalService,
  ReasonCoreService, ReasonService,
  RegulationService, StorageKey, StorageService
} from 'src/app/core/services';
import { BaseModal } from 'src/app/core/utils/BaseModal';
import { Gb } from 'src/app/core/services/growthbook.service';

@Component({
  selector: 'app-reason-create-or-update-v2',
  templateUrl: './reason-create-or-update-v2.component.html',
  styleUrls: ['./reason-create-or-update-v2.component.sass']
})
export class ReasonCreateOrUpdateV2Component extends BaseModal implements OnInit, ModalContent {
  @Input() initialState;
  public loading = false;
  public contractsPromise: Promise<Contract[]>;
  public reasonForm: FormGroup;
  public reason: Reason = new Reason();
  public contractGlobal = null;
  public isValid = false;
  public showArea = false;
  public promise: Promise<any>;
  public user: User;
  public currentPermissions = [];
  public contracts: Contract[] = [];
  public regulations: Regulation[] = [];
  public contractSelected: Contract;
  public reasonTypes = Object.values(ReasonTypes);
  public reasonTypesI18n = [];
  public reasonType = '';
  public screenArray = [
    'audit', 'typing', 'quarantine', 'validate', 'triage',
    'verifyValid', 'verifyInvalid', 'exemptPeriod', 'monitoring',
    'validateCFTV', 'verifyCFTV', 'serialNumberConfig', 'preProcessConfig'
  ];

  public moduleViolation = false;
  public moduleTraffic = false;
  private gb: Gb;
  private gbInitPromise: Promise<void>;

  get activeReasonService(): ReasonService | ReasonCoreService {
    if (this.gb?.isOn('EM-754')) {
      return this.reasonCoreService;
    }
    return this.reasonService;
  }

  constructor(
    private formBuilder: FormBuilder,
    private contractsGlobalService: ContractGlobalService,
    private storageService: StorageService,
    public modalService: ModalService,
    public contractService: ContractService,
    public regulationService: RegulationService,
    private alertService: AlertService,
    public reasonService: ReasonService,
    public reasonCoreService: ReasonCoreService
  ) {
    super(modalService);
    if (this.contractsGlobalService.contracts != null) {
      this.contractGlobal = this.contractsGlobalService.contracts.length === 1
        ? this.contractsGlobalService.contracts[0]
        : null;
    }
  }

  ngOnInit() {
    this.gb = new Gb();
    this.gbInitPromise = this.gb.init();

    this.reason = this.initialState as Reason || new Reason();
    this.currentPermissions = this.storageService.getPermissions(
      this.reason.contractId ? 'UpdateReason' : 'CreateReason'
    );
    this.user = this.storageService.get(StorageKey.currentUser);

    const modules: string[] = ((this.reason as any).applicableModules || []).map((m: string) => m.toLowerCase());
    this.moduleViolation = modules.includes('violation');
    this.moduleTraffic = modules.includes('traffic');

    this.contractsPromise = this.contractService.getAll().then(res => {
      if (this.user.superUser) {
        this.contracts = res;
      } else {
        // eslint-disable-next-line eqeqeq
        this.contracts = res?.filter(contract =>
          // eslint-disable-next-line eqeqeq
          this.currentPermissions.find(p => p.contractId == contract.id)
        );
      }
      if (this.reason.contractId == null) {
        this.reason.contractId = this.contractGlobal;
      }
      return this.contracts;
    }).then(contracts => {
      const contractId = this.reasonForm?.get('contractId')?.value;
      if (contractId) {
        this.reasonForm.get('contractId').setValue(contractId, { emitEvent: true });
      }
      return contracts;
    });

    this.createForm();
    this.getRegulations();
  }

  get isOnlyTraffic(): boolean {
    return this.moduleTraffic && !this.moduleViolation;
  }

  get showEnquadramento(): boolean {
    return !this.isOnlyTraffic;
  }

  get showTelas(): boolean {
    return !this.isOnlyTraffic;
  }

  createForm(): void {
    const screenObj = {
      audit: this.reason.screen ? this.reason.screen.includes('audit') : false,
      typing: this.reason.screen ? this.reason.screen.includes('typing') : false,
      quarantine: this.reason.screen ? this.reason.screen.includes('quarantine') : false,
      validate: this.reason.screen ? this.reason.screen.includes('validate') : false,
      triage: this.reason.screen ? this.reason.screen.includes('triage') : false,
      verifyValid: this.reason.screen ? this.reason.screen.includes('verifyValid') : false,
      verifyInvalid: this.reason.screen ? this.reason.screen.includes('verifyInvalid') : false,
      exemptPeriod: this.reason.screen ? this.reason.screen.includes('exemptPeriod') : false,
      monitoring: this.reason.screen ? this.reason.screen.includes('monitoring') : false,
      validateCFTV: this.reason.screen ? this.reason.screen.includes('validateCFTV') : false,
      verifyCFTV: this.reason.screen ? this.reason.screen.includes('verifyCFTV') : false,
      serialNumberConfig: this.reason.screen ? this.reason.screen.includes('serialNumberConfig') : false,
      preProcessConfig: this.reason.screen ? this.reason.screen.includes('preProcessConfig') : false,
    };
    const enableReason = this.reason.enabled == null ? true : this.reason.enabled;
    this.reasonForm = this.formBuilder.group({
      contractId: [this.reason.contractId || this.contractGlobal, [Validators.required]],
      code: [this.reason.code, [Validators.required]],
      clientCode: [this.reason.clientCode, [Validators.maxLength(45), Validators.pattern('^[a-zA-Z0-9]*$')]],
      name: [this.reason.name, [Validators.required]],
      regulationIds: [this.reason.regulationIds],
      type: [this.reason.type, this.isOnlyTraffic ? [] : [Validators.required]],
      enabled: [enableReason],
      audit: [screenObj.audit],
      typing: [screenObj.typing],
      quarantine: [screenObj.quarantine],
      validate: [screenObj.validate],
      triage: [screenObj.triage],
      verifyValid: [screenObj.verifyValid],
      verifyInvalid: [screenObj.verifyInvalid],
      exemptPeriod: [screenObj.exemptPeriod],
      monitoring: [screenObj.monitoring],
      validateCFTV: [screenObj.validateCFTV],
      verifyCFTV: [screenObj.verifyCFTV],
      serialNumberConfig: [screenObj.serialNumberConfig],
      preProcessConfig: [screenObj.preProcessConfig]
    });

    this.reasonForm.valueChanges.subscribe(() => {
      this.isValid = this.reasonForm.valid;
    });

    this.reasonForm.get('contractId').valueChanges.subscribe(() => {
      this.getRegulations();
    });

    if (
      this.reason.contractId && this.reason.code &&
      this.reason.name && (this.reason.type || this.isOnlyTraffic)
    ) {
      this.isValid = true;
    }

    if (this.reason?.type !== '') {
      this.reasonType = this.reason.type;
    }

    this.reasonForm.get('type').valueChanges.subscribe(type => {
      this.reasonType = type;
    });

  }

  async getRegulations() {
    const contractId = this.reasonForm.get('contractId').value;
    if (contractId) {
      await this.loadRegulationsForContract(contractId);
    }
    this.reasonForm.get('contractId').valueChanges.subscribe(async value => {
      if (value) {
        await this.loadRegulationsForContract(value);
      }
    });
  }

  private async loadRegulationsForContract(contractId: string) {
    this.loading = true;
    const contract = this.contracts.find(c => c.id === contractId);
    let params = {};
    if (contract && contract.regulationIds) {
      params = { regionId: this.contracts[0]?.regionId, 'id[in]': `[${contract.regulationIds}]` };
    } else {
      params = { regionId: this.contracts[0]?.regionId };
    }
    await this.regulationService.list(params).then(resultRegulation => {
      this.regulations = resultRegulation.result.map(item =>
        Object.assign(new Regulation(), item, { shortDescription: `${item.code} - ${item.shortDescription}` })
      );
    });
    this.loading = false;
  }

  get lastState(): any {
    throw new Error('Method not implemented.');
  }

  toggleViolation() {
    this.moduleViolation = !this.moduleViolation;
    this.updateTypeValidator();
  }

  toggleTraffic() {
    this.moduleTraffic = !this.moduleTraffic;
    this.updateTypeValidator();
  }

  private updateTypeValidator() {
    const typeControl = this.reasonForm.get('type');
    if (this.isOnlyTraffic) {
      typeControl.clearValidators();
    } else {
      typeControl.setValidators([Validators.required]);
    }
    typeControl.updateValueAndValidity();
    this.isValid = this.reasonForm.valid;
  }

  public async onAllSubmited() {
    if (this.promise != null) {
      return this.promise;
    }

    await this.gbInitPromise;

    let regulationIds: string[] | null = null;
    if (!this.isOnlyTraffic) {
      regulationIds = this.reasonForm.get('regulationIds').value || [];
      if (this.moduleViolation && regulationIds.length === 0) {
        if (this.regulations.length === 0) {
          const contractId = this.reasonForm.get('contractId').value;
          if (contractId) { await this.loadRegulationsForContract(contractId); }
        }
        regulationIds = this.regulations.map(r => r.id);
      }
    }

    let type = '';
    const typeReason = this.reasonForm.get('type').value;
    if (typeReason && typeReason !== '') {
      type = this.reasonTypesI18n.find(r =>
        r.value.trim() === typeReason || r.id.trim() === typeReason
      )?.value?.trim() || typeReason;
    }

    const enabled = this.reasonForm.get('enabled').value || false;
    const screen = [];
    for (const screenItem of this.screenArray) {
      if (this.reasonForm.get(screenItem).value === true) {
        screen.push(screenItem);
      }
    }

    const applicableModules: string[] = [];
    if (this.moduleViolation) { applicableModules.push('violation'); }
    if (this.moduleTraffic) { applicableModules.push('traffic'); }

    const data: any = {
      id: this.reason.id,
      contractId: this.reasonForm.get('contractId').value,
      code: this.reasonForm.get('code').value,
      clientCode: (this.reasonForm.get('clientCode').value || '').replace(/\s/g, ''),
      name: this.reasonForm.get('name').value,
      type: type || '',
      enabled,
      regulationIds,
      screen,
      applicableModules
    };

    const reason = Reason.create(data);
    if (reason.id != null) {
      this.promise = this.activeReasonService.update(reason);
    } else {
      this.promise = this.activeReasonService.create(reason);
    }

    this.promise.then(res => {
      this.reason = Reason.create(res);
      this.alertService.show(new AlertItem('ReasonSaved', AlertType.success));
      return res;
    }).catch(error => {
      if (error.status === 409) {
        this.alertService.show(new AlertItem('ReasonSaveConflict', AlertType.danger));
      } else {
        this.alertService.show(new AlertItem('ReasonSaveError', AlertType.danger));
      }
      throw error;
    }).finally(() => {
      this.promise = null;
    });
    return this.promise;
  }

  toggleCollapse(show) {
    this.showArea = !show;
  }
}
